import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, delay, first, map, switchMap, take, tap } from 'rxjs/operators';
import { ApiUserService } from 'src/app/api/api/api-user/api-user.service';
import * as RootActions from "src/app/store/root.actions";
import { RootState } from '../../root.reducer';
import * as UserActions from '../actions/user.actions';
import { UserCredential } from 'src/app/models/data/user.model';
import { NzModalService } from 'ng-zorro-antd/modal';
import { localStorageIsValid, notEmptyNullUndefined } from 'src/app/helpers/utils';
import { selectUserCredentials } from '../selectors/user.selectors';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TokenCookieService } from 'src/app/helpers/storage service/cookies/token-cookies.service';

@Injectable()
export class UserEffects {

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadUsers),
      concatMap(() =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        EMPTY.pipe(
          map(data => UserActions.loadUsersSuccess({ data })),
          catchError(error => of(UserActions.loadUsersFailure({ error }))))
      )
    );
  });

  /**
   * auto sign in will run when open the web
   * update 13/09/2022 by ericZ, no more cookies and use local storage now
   *
   * @memberof UserEffects
   */
  autoSignIn$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.autoSignIn),
    tap(() => {
      // if (this.cookies.check(`omis_remember_account`) === true) {
      //   const token: UserCredential = JSON.parse(this.cookies.get(`omis_remember_account`))
      //   this.store.dispatch(UserActions.setCredentials({ token }))
      // } else {
      // }
      if (this.tokenCookieService.hasToken()) {
        const token = this.tokenCookieService.getToken()
        this.store.dispatch(UserActions.setCredentials({token: { jwtToken: token }}))
      }
    })
  ), { dispatch: false })

  /**
   * Once credentials are set into store,
   * store credentials into persisted storage,
   * fetch all entities,
   * and fetch user info.
   */
  setCredentials$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.setCredentials),
    map(() => UserActions.fetchTSMSUserInfo()),
  ),
  )

  fetchTSMSUserInfo$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.fetchTSMSUserInfo),
    switchMap(() => this.apiUserService.fetchTsmsUserInfo$().pipe(
      catchError(error => {
        // Clear loading state on error
        this.store.dispatch(UserActions.clearuiloginLoading())
        this.notification.error(
          'Authentication Error',
          'Failed to fetch user information. Please try again.'
        )
        return EMPTY
      })
    )),
    map((tsmsUser) => UserActions.setTimeslotBookingUser({timeslotBookingUser: tsmsUser})),
  ))


  /**
   * Remove token from persisted storage.
   * update 31/08/2021
   *  reason: when open the component and get 401,
   *  user re-login will make the redux crash because the redux is clear and DOM load undefined.
   *  It will cause the Redux crash.
   *
   *  To solve this problem, just Try to reload the whole app and clear the credentials inseide the memory
   * update 13/09/2022 by ericZ, no more cookies and use local storage now

   */
  invalidateToken$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.invalidateToken),
    tap(() => {
      this.modal.error({
        nzTitle: 'Error 401',
        nzContent: 'Log-in details have expired, please log-in again.',
        nzOnOk: () => {
          this.store.dispatch(RootActions.purgeAllEntities())
          this.router.navigate(["../log-in"], { replaceUrl: true })
        }
      })
      //clear cookies
      // if (this.cookies.check(`omis_remember_account`) === true) {
      //   this.cookies.delete(`omis_remember_account`);
      // }
      if (localStorageIsValid(`omis_remember_account`)) {
        localStorage.removeItem(`omis_remember_account`)
      }
    }),
  ), { dispatch: false })

  /**
   * logout will change page
   * delete some of the data inside redux
   * remove the login cookie
   * update 13/09/2022 by ericZ, no more cookies and use local storage now
   */
  logOut$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.logOut),
    tap(() => this.router.navigate(["../log-in"], { replaceUrl: true })),
    // tap(() => this.storageService.remove("credentials")),
    tap(() => {
      this.store.dispatch(RootActions.purgeAllEntities())
      //clear cookies
      // if (this.cookies.check(`omis_remember_account`) === true) {
      //   this.cookies.delete(`omis_remember_account`);
      // }
      if (localStorageIsValid(`omis_remember_account`)) {
        localStorage.removeItem(`omis_remember_account`)
      }
    }),
    map(() => UserActions.signOut()),
  ))

  /**
   * Once user info snapshot is applied, clear snapshot.
   */
  restoreUserSnapshot$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.restoreUserSnapshot),
    map(() => UserActions.clearUserSnapshot()),
  ))

  registNewAccoutFail$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.registNewAccoutFail),
    tap(() => {
      //add loading cancel?
      this.notification.create(
        'error',
        'Error',
        `Fail to create new account`
      );
    })
  ), { dispatch: false })

  /**
   * once user click the accept button in cookie agreement
   * will check user have log in or not,
   * if log in will record the user token
   * update 13/09/2022 by ericZ, no more cookies and use local storage now

   */
  loadUserInStateAndSaveToCookie$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.loadUserInStateAndSaveToCookie),
    switchMap(() => this.store.select(selectUserCredentials).pipe(first())),
    // tap((credential)=>notEmptyNullUndefined(credential) ? this.cookies.set(`omis_remember_account`, JSON.stringify(credential), null, '/') : {})
    tap(credential => notEmptyNullUndefined(credential) ? localStorage.setItem(`omis_remember_account`, JSON.stringify(credential)) : {})
  ), { dispatch: false })

  constructor(
    private actions$: Actions,
    private store: Store<RootState>,
    private apiUserService: ApiUserService,
    private router: Router,
    private route: ActivatedRoute,
    private cookies: CookieService,
    private notification: NzNotificationService,
    private modal: NzModalService,
    private tokenCookieService: TokenCookieService,
  ) { }

}
